/**
 * Email OTP Service
 *
 * Integrates with Ory Kratos code method for email verification
 *
 * Email OTP via Ory code authenticator
 */
import { oryService } from '../ory/ory-service';
export var EmailOTPErrorCode;
(function (EmailOTPErrorCode) {
    EmailOTPErrorCode["FLOW_EXPIRED"] = "FLOW_EXPIRED";
    EmailOTPErrorCode["OTP_SEND_FAILED"] = "OTP_SEND_FAILED";
    EmailOTPErrorCode["OTP_VERIFY_FAILED"] = "OTP_VERIFY_FAILED";
    EmailOTPErrorCode["INVALID_CODE"] = "INVALID_CODE";
    EmailOTPErrorCode["RATE_LIMITED"] = "RATE_LIMITED";
    EmailOTPErrorCode["NETWORK_ERROR"] = "NETWORK_ERROR";
    EmailOTPErrorCode["UNEXPECTED_ERROR"] = "UNEXPECTED_ERROR";
})(EmailOTPErrorCode || (EmailOTPErrorCode = {}));
/**
 * Service for handling email OTP authentication
 *
 * Uses Ory Kratos code method
 */
export class EmailOTPService {
    static instance;
    loginFlow = null;
    constructor() { }
    static getInstance() {
        if (!EmailOTPService.instance) {
            EmailOTPService.instance = new EmailOTPService();
        }
        return EmailOTPService.instance;
    }
    /**
     * Request OTP code to be sent to email
     *
     * Uses Ory Kratos code method
     */
    async requestOTP(request) {
        try {
            // Create or get login flow
            if (!this.loginFlow || this.isFlowExpired(this.loginFlow)) {
                this.loginFlow = await oryService.createLoginFlow();
            }
            // Extract CSRF token from flow
            const csrfToken = this.getCSRFToken(this.loginFlow);
            // Submit identifier to trigger email code
            const updateBody = {
                method: 'code',
                identifier: request.email,
                csrf_token: csrfToken
            };
            const result = await oryService.updateLoginFlow(this.loginFlow.id, updateBody);
            // Check if we got a new flow (need to enter code)
            if ('ui' in result) {
                this.loginFlow = result;
                return {
                    success: true,
                    flowId: this.loginFlow.id,
                    expiresAt: new Date(this.loginFlow.expires_at)
                };
            }
            else {
                // Unexpected: login completed without code
                return {
                    success: false,
                    error: 'Unexpected response from authentication service',
                    errorCode: EmailOTPErrorCode.UNEXPECTED_ERROR
                };
            }
        }
        catch (error) {
            this.logError('Failed to request OTP', error);
            const { message, code } = this.getErrorDetails(error);
            return {
                success: false,
                error: message,
                errorCode: code
            };
        }
    }
    /**
     * Verify OTP code
     *
     * Verifies email OTP with Ory
     */
    async verifyOTP(request) {
        try {
            // Use existing flow or create new one
            if (!this.loginFlow || this.isFlowExpired(this.loginFlow)) {
                return {
                    success: false,
                    error: 'Session expired. Please request a new code.',
                    errorCode: EmailOTPErrorCode.FLOW_EXPIRED
                };
            }
            // Extract CSRF token
            const csrfToken = this.getCSRFToken(this.loginFlow);
            // Submit code for verification
            const updateBody = {
                method: 'code',
                code: request.code,
                identifier: request.email,
                csrf_token: csrfToken
            };
            const result = await oryService.updateLoginFlow(this.loginFlow.id, updateBody);
            // Check if login was successful
            if ('session' in result) {
                // Clear flow after successful login
                this.loginFlow = null;
                return {
                    success: true,
                    session: result.session
                };
            }
            else if ('ui' in result) {
                // Update flow (might have errors)
                this.loginFlow = result;
                // Check for errors in the flow
                const errors = this.getFlowErrors(this.loginFlow);
                if (errors.length > 0) {
                    return {
                        success: false,
                        error: errors[0],
                        errorCode: this.determineErrorCode(errors[0])
                    };
                }
                // Still in progress
                return {
                    success: false,
                    error: 'Additional verification required',
                    errorCode: EmailOTPErrorCode.UNEXPECTED_ERROR
                };
            }
            else {
                return {
                    success: false,
                    error: 'Unexpected response from authentication service',
                    errorCode: EmailOTPErrorCode.UNEXPECTED_ERROR
                };
            }
        }
        catch (error) {
            this.logError('Failed to verify OTP', error);
            const { message, code } = this.getErrorDetails(error);
            return {
                success: false,
                error: message,
                errorCode: code
            };
        }
    }
    /**
     * Resend OTP code
     *
     * Request new OTP code
     */
    async resendOTP(email) {
        // Create new flow to trigger new code
        this.loginFlow = null;
        return this.requestOTP({ email });
    }
    /**
     * Check if flow has expired
     */
    isFlowExpired(flow) {
        const expiresAt = new Date(flow.expires_at);
        return expiresAt <= new Date();
    }
    /**
     * Extract CSRF token from flow
     */
    getCSRFToken(flow) {
        const csrfNode = flow.ui.nodes.find(node => node.attributes.node_type === 'input' &&
            node.attributes.name === 'csrf_token');
        return csrfNode ? csrfNode.attributes.value : '';
    }
    /**
     * Extract errors from flow
     */
    getFlowErrors(flow) {
        const errors = [];
        // Check for general messages
        if (flow.ui.messages) {
            errors.push(...flow.ui.messages.map(msg => msg.text));
        }
        // Check for field-specific errors
        flow.ui.nodes.forEach(node => {
            if (node.messages) {
                errors.push(...node.messages.map((msg) => msg.text));
            }
        });
        return errors;
    }
    /**
     * Get user-friendly error message and code
     */
    getErrorDetails(error) {
        // Check for Ory-specific error responses
        if (error.response?.data?.error?.message) {
            const oryMessage = error.response.data.error.message.toLowerCase();
            if (oryMessage.includes('expired')) {
                return {
                    message: 'Your verification session has expired. Please request a new code.',
                    code: EmailOTPErrorCode.FLOW_EXPIRED
                };
            }
            if (oryMessage.includes('rate limit')) {
                return {
                    message: 'Too many attempts. Please wait a moment before trying again.',
                    code: EmailOTPErrorCode.RATE_LIMITED
                };
            }
            if (oryMessage.includes('invalid') || oryMessage.includes('incorrect')) {
                return {
                    message: 'The verification code is incorrect. Please try again.',
                    code: EmailOTPErrorCode.INVALID_CODE
                };
            }
        }
        // Network errors
        if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
            return {
                message: 'Network timeout. Please check your connection and try again.',
                code: EmailOTPErrorCode.NETWORK_ERROR
            };
        }
        if (!navigator.onLine) {
            return {
                message: 'No internet connection. Please check your network and try again.',
                code: EmailOTPErrorCode.NETWORK_ERROR
            };
        }
        // Default error
        return {
            message: 'Failed to send verification code. Please try again.',
            code: EmailOTPErrorCode.OTP_SEND_FAILED
        };
    }
    /**
     * Determine error code from error message
     */
    determineErrorCode(errorMessage) {
        const message = errorMessage.toLowerCase();
        if (message.includes('expired'))
            return EmailOTPErrorCode.FLOW_EXPIRED;
        if (message.includes('invalid') || message.includes('incorrect'))
            return EmailOTPErrorCode.INVALID_CODE;
        if (message.includes('rate limit'))
            return EmailOTPErrorCode.RATE_LIMITED;
        return EmailOTPErrorCode.OTP_VERIFY_FAILED;
    }
    /**
     * Log error conditionally based on environment
     */
    logError(context, error) {
        if (process.env.NODE_ENV === 'development') {
            console.error(`[EmailOTPService] ${context}:`, error);
        }
    }
    /**
     * Clear current flow
     *
     * Reset flow state
     */
    clearFlow() {
        this.loginFlow = null;
    }
}
